import axios from 'axios';
import { Clickable, Col, Row, Text } from 'components';
import { useOutsideClick } from 'core';
import { Api, getToken } from 'lib';
import React, { useEffect, useRef, useState } from 'react';
import { IrreversableModal } from 'screens/Admin/components';
import { store } from 'store';
import styled from 'styled-components';

const API_URL = process.env.REACT_APP_API_URL;
const DEFAULT_MESSAGE = '#playaz4playaz';
const DOWNLOAD_ENDPOINT = 'admin/downloadShoutout';
const DEFAULT_ERROR_MESSAGE = 'Something went wrong.\nPlease try again later.';
const UNSUPPORTED_ERROR_MESSAGE = 'Only text shoutouts can be downloaded.';
// a text shoutout is delivered as the rendered ribbon board banner
const BANNER_EXTENSION = 'png';
const Container = styled.div`
  width: 110px;
  background-color: #fff;
  box-shadow: 0 0 16px rgba(0, 0, 0, 0.1);
  z-index: 0;
  margin: -20px 0 0 -40px;
  height: 20px;
  position: relative;
  visibility: ${(props) => (props.visible ? 'visible' : 'hidden')};
`;

/**
 * File name for a downloaded shoutout. The server names the file in its
 * Content-Disposition; the banner extension covers a response whose header is
 * not readable.
 * @param {Object} response
 * @param {string} shoutoutId
 * @returns {string}
 */
function downloadFileName(response, shoutoutId) {
  const disposition = response?.headers?.['content-disposition'],
    named = disposition && /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);

  if (named) {
    return decodeURIComponent(named[1]);
  }

  return `${shoutoutId}-shoutout.${BANNER_EXTENSION}`;
}

const Menu = ({ shoutoutId, message, visible, close, onDelete }) => {
  const containerRef = useRef(),
    [failure, setFailure] = useState({}),
    [downloading, setDownloading] = useState(false),
    [deleting, setDeleting] = useState(false),
    [showDownloadAlert, setShowDownloadAlert] = useState(false),
    [showDeleteAlert, setShowDeleteAlert] = useState(false);

  useOutsideClick(containerRef, () => visible && !downloading && close(), [
    'confirm-action',
    'modal-bg',
    'modal-container',
    'modal-popup'
  ]);

  useEffect(() => {
    if (failure?.err) {
      store.dispatch({
        type: 'HANDLE_RES',
        payload: failure
      });
      wrapUp();
    }
  }, [failure]);

  /**
   * Helper function to reset all states
   */
  function wrapUp() {
    setShowDownloadAlert(false);
    setShowDeleteAlert(false);
    setDownloading(false);
    setDeleting(false);
    setFailure({});
    close();
  }

  /**
   * Helper function to save a downloaded file
   * @param {Blob} blob
   * @param {string} fileName
   */
  function saveFile(blob, fileName) {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  /**
   * Facilitate download of a selected shoutout as the rendered banner. Only
   * text shoutouts can be rendered; the server rejects the rest.
   * @returns {Promise}
   */
  async function download() {
    setDownloading(true);

    try {
      const token = await getToken();
      const result = await axios.get(`${API_URL}/${DOWNLOAD_ENDPOINT}/${shoutoutId}`, {
        responseType: 'blob',
        params: { leftMessage: DEFAULT_MESSAGE },
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      saveFile(result.data, downloadFileName(result, shoutoutId));
      wrapUp();
    } catch (error) {
      const unsupported = error?.response?.status === 403;

      setFailure({
        err: unsupported ? UNSUPPORTED_ERROR_MESSAGE : DEFAULT_ERROR_MESSAGE,
        reference: error
      });
    }
  }

  /**
   * Delete shoutout via admin shoutouts delete endpoint
   */
  async function handleDelete() {
    setDeleting(true);
    const res = await Api.delete(`/admin/shoutouts/delete/${shoutoutId}`);
    setDeleting(false);
    if (res) {
      store.dispatch({ type: 'ADMIN_REMOVE_SHOUTOUT', payload: shoutoutId });
      store.dispatch({ type: 'HANDLE_RES', payload: { res: 'Shoutout deleted successfully' } });
      onDelete?.(shoutoutId);
    }
    wrapUp();
  }

  if (!visible) {
    return null;
  }

  return (
    <React.Fragment>
      <Container ref={containerRef} visible={!showDownloadAlert && !showDeleteAlert}>
        <Col>
          <Clickable
            onClick={() => {
              setShowDownloadAlert(true);
            }}
          >
            <Row noFlex ht="48px" bg="#fff" center>
              <Text med pad="0" marg="0 0 0 16px">
                Download
              </Text>
            </Row>
          </Clickable>
          <Clickable
            onClick={() => {
              setShowDeleteAlert(true);
            }}
          >
            <Row noFlex ht="48px" bg="#fff" center>
              <Text med pad="0" marg="0 0 0 16px" col="#E53935">
                Delete
              </Text>
            </Row>
          </Clickable>
        </Col>
      </Container>
      <IrreversableModal
        visible={showDownloadAlert}
        onConfirm={download}
        closeModal={() => {
          setShowDownloadAlert(false);
        }}
        noOutsideClick={true}
        confirmNoClose={true}
        waiting={downloading}
        confirmText={'Download'}
        topText={'Shoutout:'}
        text={message}
        heading={
          downloading
            ? 'Preparing the shoutout download. Please wait.'
            : 'You are about to download this shoutout.'
        }
      />
      <IrreversableModal
        visible={showDeleteAlert}
        onConfirm={handleDelete}
        closeModal={() => {
          setShowDeleteAlert(false);
        }}
        noOutsideClick={true}
        waiting={deleting}
        confirmText={'Delete'}
        text={message}
        heading={'You are about to delete this shoutout.'}
      />
    </React.Fragment>
  );
};

export default Menu;
